import { useState } from "react";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { useAuthUser, useAuthStatus } from "../lib/auth";
import { useToast } from "../lib/toast";
import { apiFetch, ApiError } from "../lib/api";

interface FeedbackButtonProps {
  aboveTabBar?: boolean;
}

export function FeedbackButton({ aboveTabBar = false }: FeedbackButtonProps) {
  const { user, tenantId } = useAuthUser();
  const { status } = useAuthStatus();
  const toast = useToast();

  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Only show for authenticated users
  if (status !== "authenticated" || !user || !tenantId) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      toast.error("Please enter a message");
      return;
    }

    setIsSubmitting(true);

    try {
      await apiFetch("/api/v1/feedback", {
        method: "POST",
        body: {
          message: trimmedMessage,
          page_url: window.location.href,
          user_agent: navigator.userAgent,
        },
      });

      toast.success("Feedback sent", "Thank you for your feedback!");
      setMessage("");
      setIsOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        toast.error("Too many submissions", "Please wait before sending more feedback.");
      } else {
        toast.error("Failed to send feedback", "Please try again later.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed right-4 z-40 flex h-8 w-8 items-center justify-center rounded-full bg-surface text-muted ring-1 ring-border/50 transition-all hover:bg-surface2 hover:text-foreground hover:ring-border focus:outline-none focus:ring-2 focus:ring-accent/30 ${aboveTabBar ? "bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px)+3rem)]" : "bottom-4"}`}
        aria-label="Send feedback"
      >
        <MessageSquare className="h-4 w-4" />
      </button>

      {/* Modal overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-overlay/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-bg shadow-2xl ring-1 ring-border/70">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border/70 px-4 py-3">
              <h2 className="text-lg font-semibold text-foreground">
                Send Feedback
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-lg p-1 text-muted hover:bg-surface hover:text-foreground"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="p-4">
              <label className="block">
                <span className="text-sm font-medium text-foreground">
                  How can we improve?
                </span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  className="mt-1 block w-full rounded-lg shadow-input bg-surface px-3 py-2 text-sm text-foreground placeholder-muted focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  placeholder="Share your thoughts, report bugs, or suggest features..."
                  disabled={isSubmitting}
                  autoFocus
                />
              </label>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-foreground hover:bg-surface"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !message.trim()}
                  className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-foreground hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Send Feedback
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
